// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import blakejs from 'blakejs';
const { blake2b, blake2bInit, blake2bUpdate, blake2bFinal } = blakejs;
import { bytes, hex } from './convert.js';
import Curve25519 from './curve25519.js';
const curve = new Curve25519();
const X = curve.gf([0xd51a, 0x8f25, 0x2d60, 0xc956, 0xa7b2, 0x9525, 0xc760, 0x692c, 0xdc5c, 0xfdd6, 0xe231, 0xc0a4, 0x53fe, 0xcd6e, 0x36d3, 0x2169]);
const Y = curve.gf([0x6658, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666, 0x6666]);
const L = new Uint8Array([0xed, 0xd3, 0xf5, 0x5c, 0x1a, 0x63, 0x12, 0x58, 0xd6, 0x9c, 0xf7, 0xa2, 0xde, 0xf9, 0xde, 0x14, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0x10]);
/**
* Generate a public key from a private key using the Ed25519 algorithm. The key
* should be a cryptographically strong random value.
*
* @param {string} privateKey - 32-byte hexadecimal private key
* @returns {string} 32-byte hexadecimal public key
*/
function getPublicKey(privateKey) {
    const pk = new Uint8Array(32);
    const p = [curve.gf(), curve.gf(), curve.gf(), curve.gf()];
    const h = blake2b(hex.toBytes(privateKey), undefined, 64).slice(0, 32);
    h[0] &= 0xf8;
    h[31] &= 0x7f;
    h[31] |= 0x40;
    scalarbase(p, h);
    pack(pk, p);
    return bytes.toHex(pk);
}
/**
* Convert Ed25519 keypair to Curve25519 keypair suitable for Diffie-Hellman key exchange
*
* @param {KeyPair} keyPair - Ed25519 keypair
* @returns {KeyPair} Curve25519 keypair
*/
function convertKeys(keyPair) {
    const pubKeyBuf = hex.toBytes(keyPair.publicKey);
    const ab = curve.convertEd25519PublicKeyToCurve25519(pubKeyBuf);
    if (ab == null) {
        throw new Error('Invalid key pair');
    }
    const publicKey = bytes.toHex(ab) ?? '';
    if (publicKey === '') {
        throw new Error('Invalid key pair');
    }
    const privKeyBuf = hex.toBytes(keyPair.privateKey);
    const privateKey = bytes.toHex(curve.convertEd25519PrivateKeyToCurve25519(privKeyBuf));
    return {
        publicKey,
        privateKey,
    };
}
/**
* Generate a message signature
* @param {Uint8Array} msg - Message to be signed
* @param {Uint8Array} privateKey - Private key to use for signing
* @returns {Uint8Array} 64-byte signature
*/
function sign(msg, privateKey) {
    return naclSign(msg, privateKey).slice(0, 64);
}
/**
* Verify a message signature
* @param {Uint8Array} msg - Message to be signed as byte array
* @param {Uint8Array} publicKey - Public key as byte array
* @param {Uint8Array} signature - Signature as byte array
* @returns {boolean} True if `msg` was signed by `publicKey`, else false
*/
function verify(msg, publicKey, signature) {
    const CURVE = curve;
    const p = [CURVE.gf(), CURVE.gf(), CURVE.gf(), CURVE.gf()];
    const q = [CURVE.gf(), CURVE.gf(), CURVE.gf(), CURVE.gf()];
    if (signature.length !== 64) {
        return false;
    }
    if (publicKey.length !== 32) {
        return false;
    }
    if (CURVE.unpackNeg(q, publicKey)) {
        return false;
    }
    const ctx = blake2bInit(64, undefined);
    blake2bUpdate(ctx, signature.subarray(0, 32));
    blake2bUpdate(ctx, publicKey);
    blake2bUpdate(ctx, msg);
    let k = blake2bFinal(ctx);
    reduce(k);
    scalarmult(p, q, k);
    let t = new Uint8Array(32);
    scalarbase(q, signature.subarray(32));
    CURVE.add(p, q);
    pack(t, p);
    if (signature.subarray(0, 32).length !== t.length)
        return false;
    for (let i = 0; i < t.length; i++) {
        if (signature.subarray(0, 32)[i] !== t[i])
            return false;
    }
    return true;
}
function pack(r, p) {
    const CURVE = curve;
    const tx = CURVE.gf(), ty = CURVE.gf(), zi = CURVE.gf();
    CURVE.inv25519(zi, p[2]);
    CURVE.M(tx, p[0], zi);
    CURVE.M(ty, p[1], zi);
    CURVE.pack25519(r, ty);
    r[31] ^= CURVE.par25519(tx) << 7;
}
function modL(r, x) {
    let carry, i, j, k;
    for (i = 63; i >= 32; --i) {
        carry = 0;
        for (j = i - 32, k = i - 12; j < k; ++j) {
            x[j] += carry - 16 * x[i] * L[j - (i - 32)];
            carry = (x[j] + 128) >> 8;
            x[j] -= carry * 256;
        }
        x[j] += carry;
        x[i] = 0;
    }
    carry = 0;
    for (j = 0; j < 32; j++) {
        x[j] += carry - (x[31] >> 4) * L[j];
        carry = x[j] >> 8;
        x[j] &= 255;
    }
    for (j = 0; j < 32; j++) {
        x[j] -= carry * L[j];
    }
    for (i = 0; i < 32; i++) {
        x[i + 1] += x[i] >>> 8;
        r[i] = x[i] & 0xff;
    }
}
function reduce(r) {
    const x = new Uint32Array(64);
    for (let i = 0; i < 64; i++) {
        x[i] = r[i];
    }
    modL(r, x);
}
function scalarmult(p, q, s) {
    const CURVE = curve;
    CURVE.set25519(p[0], CURVE.gf0);
    CURVE.set25519(p[1], CURVE.gf1);
    CURVE.set25519(p[2], CURVE.gf1);
    CURVE.set25519(p[3], CURVE.gf0);
    for (let i = 255; i >= 0; --i) {
        const b = (s[(i / 8) | 0] >>> (i & 7)) & 1;
        CURVE.cswap(p, q, b);
        CURVE.add(q, p);
        CURVE.add(p, p);
        CURVE.cswap(p, q, b);
    }
}
function scalarbase(p, s) {
    const CURVE = curve;
    const q = [CURVE.gf(), CURVE.gf(), CURVE.gf(), CURVE.gf()];
    CURVE.set25519(q[0], X);
    CURVE.set25519(q[1], Y);
    CURVE.set25519(q[2], CURVE.gf1);
    CURVE.M(q[3], X, Y);
    scalarmult(p, q, s);
}
function naclSign(msg, privateKey) {
    if (privateKey.length !== 32) {
        throw new Error('bad private key size');
    }
    const signedMsg = new Uint8Array(64 + msg.length);
    cryptoSign(signedMsg, msg, msg.length, privateKey);
    return signedMsg;
}
function cryptoSign(sm, m, n, sk) {
    const CURVE = curve;
    const d = new Uint8Array(64);
    const h = new Uint8Array(64);
    const r = new Uint8Array(64);
    const x = new Float64Array(64);
    const p = [CURVE.gf(), CURVE.gf(), CURVE.gf(), CURVE.gf()];
    let i;
    let j;
    const pubKey = getPublicKey(bytes.toHex(sk));
    const pk = hex.toBytes(pubKey);
    curve.cryptoHash(d, sk, 32);
    d[0] &= 248;
    d[31] &= 127;
    d[31] |= 64;
    const smlen = n + 64;
    for (i = 0; i < n; i++) {
        sm[64 + i] = m[i];
    }
    for (i = 0; i < 32; i++) {
        sm[32 + i] = d[32 + i];
    }
    curve.cryptoHash(r, sm.subarray(32), n + 32);
    reduce(r);
    scalarbase(p, r);
    pack(sm, p);
    for (i = 32; i < 64; i++) {
        sm[i] = pk[i - 32];
    }
    curve.cryptoHash(h, sm, n + 64);
    reduce(h);
    for (i = 0; i < 64; i++) {
        x[i] = 0;
    }
    for (i = 0; i < 32; i++) {
        x[i] = r[i];
    }
    for (i = 0; i < 32; i++) {
        for (j = 0; j < 32; j++) {
            x[i + j] += h[i] * d[j];
        }
    }
    modL(sm.subarray(32), x);
    return smlen;
}
export default { convertKeys, getPublicKey, sign, verify };
